import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import type { FindingDocType, FindingProfileDocType } from '@ast-data/shared';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { locationLabel } from './locationLabel';

/** The record a "Letzte vergebene Nummer" points at, if one exists. */
export interface RecordNumberTarget {
    /** The record's OWN project — a family shares numbers across measures. */
    projectId: string;
    findingId: string;
    /** Set for a Profil; absent when the target is the Befund itself. */
    profileId?: string;
    /** What the link opens, as a Lagezuordnung ("Bef. 7012, Pr. 3"). */
    label: string;
}

export interface DiaryNumberLinks {
    finding: RecordNumberTarget | null;
    profile: RecordNumberTarget | null;
}

/** Compare two records by when they were recorded, newest first. */
function byRecordedAtDesc(
    firstDiary: { recordedAt: string },
    secondDiary: { recordedAt: string }
): number {
    return (secondDiary.recordedAt || '').localeCompare(
        firstDiary.recordedAt || ''
    );
}

/**
 * The Befund and the Profil the day's "Letzte vergebene Nrn." name — so the
 * Tagebuch can link straight to them. The numbers themselves are typed by hand;
 * this only resolves what stands in the form, family-wide.
 *
 * Befundnummern are unique across the family, so that lookup is exact.
 * PROFILNUMMERN ARE NOT — they are unique only within their Befund, so several
 * Befunde may each have a "Profil 3". The most recently recorded one wins,
 * matching what "last issued" means, and the label names its Befund.
 */
export function useDiaryNumberLinks(
    projectId: string,
    findingNumber: string,
    profileNumber: string
): DiaryNumberLinks {
    const familyIds = useFamilyProjectIds(projectId);
    const findingsQuery = useStableQuery<FindingDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findingsDocs } = useLiveRxQuery<FindingDocType>({
        collection: 'findings',
        query: findingsQuery
    });
    const findings = useMemo(
        () => findingsDocs.map((doc) => doc.toMutableJSON() as FindingDocType),
        [findingsDocs]
    );
    const profilesQuery = useStableQuery<FindingProfileDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: profilesDocs } = useLiveRxQuery<FindingProfileDocType>({
        collection: 'finding_profiles',
        query: profilesQuery
    });
    const profiles = useMemo(
        () =>
            profilesDocs.map(
                (doc) => doc.toMutableJSON() as FindingProfileDocType
            ),
        [profilesDocs]
    );

    const wantedFinding = findingNumber.trim();
    const wantedProfile = profileNumber.trim();

    return useMemo(() => {
        const findingById = new Map(
            findings.map((finding) => [finding.id, finding])
        );

        const matchedFinding = wantedFinding
            ? (findings.find(
                  (finding) =>
                      (finding.findingNumber ?? '').trim() === wantedFinding
              ) ?? null)
            : null;

        const matchedProfile = wantedProfile
            ? ([...profiles]
                  .filter(
                      (profile) =>
                          (profile.profileNumber ?? '').trim() === wantedProfile
                  )
                  .sort(byRecordedAtDesc)[0] ?? null)
            : null;
        const profileParent = matchedProfile
            ? findingById.get(matchedProfile.findingId)
            : undefined;

        return {
            finding: matchedFinding
                ? {
                      projectId: matchedFinding.projectId,
                      findingId: matchedFinding.id,
                      label: locationLabel({
                          findingNumber: matchedFinding.findingNumber ?? ''
                      })
                  }
                : null,
            // Without its Befund the profile has no route to sit under and no
            // name to show, so it is not offered.
            profile:
                matchedProfile && profileParent
                    ? {
                          projectId: matchedProfile.projectId,
                          findingId: matchedProfile.findingId,
                          profileId: matchedProfile.id,
                          label: locationLabel({
                              findingNumber: profileParent.findingNumber ?? '',
                              area: matchedProfile.area,
                              profileNumber: matchedProfile.profileNumber ?? '',
                              part: matchedProfile.partLabel ?? ''
                          })
                      }
                    : null
        };
    }, [findings, profiles, wantedFinding, wantedProfile]);
}
